import { PERSONA_ID, getPerson } from '@/demo/procurement/data/company';
import { fmtMoney } from '@/demo/procurement/data/format';
import { getMaterial } from '@/demo/procurement/data/materials';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import type {
  ActivityEntry,
  AgentDef,
  AgentId,
  AgentStatus,
  Approver,
  AutonomyLevel,
  EntityRef,
  Guardrail,
  LimitUnit,
  ReplayDef,
  SopStep,
} from './agents-types';

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** '4 min ago', '1 h 40 min ago', '3 days ago'. */
export function fmtAgo(atMs: number, nowMs: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((nowMs - atMs) / MS_PER_MINUTE));
  if (minutes < 1) return 'just now';
  if (minutes < MINUTES_PER_HOUR) return `${minutes} min ago`;
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  if (hours < 6) {
    const rest = minutes % MINUTES_PER_HOUR;
    return rest === 0 ? `${hours} h ago` : `${hours} h ${rest} min ago`;
  }
  if (hours < HOURS_PER_DAY) return `${hours} h ago`;
  const days = Math.floor(hours / HOURS_PER_DAY);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/** 'Oct 9, 2:14 PM' for tooltips. */
export function fmtClock(atMs: number): string {
  return new Date(atMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const AUTONOMY_LABEL: Record<AutonomyLevel, string> = {
  suggest: 'Suggest only',
  approval: 'Act with approval',
  autonomous: 'Autonomous within limits',
};

export const AUTONOMY_SHORT_LABEL: Record<AutonomyLevel, string> = {
  suggest: 'Suggest only',
  approval: 'With approval',
  autonomous: 'Autonomous',
};

export const AUTONOMY_EXPLANATION: Record<AutonomyLevel, string> = {
  suggest:
    'The agent prepares drafts and recommendations. A person does every send, change and payment.',
  approval:
    'The agent prepares the action and a named approver confirms it before anything is sent, changed or paid.',
  autonomous:
    'The agent acts on its own inside the limits below and escalates anything beyond them to a person.',
};

export const STATUS_LABEL: Record<AgentStatus, string> = {
  active: 'Active',
  shadow: 'Practice mode',
  paused: 'Paused',
};

export function autonomyRank(level: AutonomyLevel): number {
  if (level === 'suggest') return 1;
  return level === 'approval' ? 2 : 3;
}

export function fmtLimitValue(unit: LimitUnit, value: number): string {
  if (unit === 'usd') return fmtMoney(value);
  if (unit === 'pct') return `${value}%`;
  return String(value);
}

/** The guardrail as one sentence, with the current value for a limit. */
export function guardrailSentence(
  guardrail: Guardrail,
  value?: number,
): string {
  const { control } = guardrail;
  if (control.type !== 'limit') return guardrail.before;
  const shown = fmtLimitValue(control.unit, value ?? control.value);
  return [guardrail.before, shown, guardrail.after]
    .filter((part): part is string => Boolean(part))
    .join(' ');
}

export function clampLimit(guardrail: Guardrail, value: number): number {
  const { control } = guardrail;
  if (control.type !== 'limit') return value;
  return Math.min(control.max, Math.max(control.min, value));
}

export interface RosterTotals {
  actions: number;
  hoursSaved: number;
  approvals: number;
  approvalRatePct: number;
}

export function rosterTotals(agents: AgentDef[]): RosterTotals {
  const actions = agents.reduce((sum, a) => sum + a.metrics.actions, 0);
  const hoursSaved = agents.reduce((sum, a) => sum + a.metrics.hoursSaved, 0);
  const approvals = agents.reduce(
    (sum, a) => sum + a.metrics.approvalsRequired,
    0,
  );
  return {
    actions,
    hoursSaved,
    approvals,
    approvalRatePct: actions === 0 ? 0 : (approvals / actions) * 100,
  };
}

/** Steps as editable plain text: a numbered title, then its instruction. */
export function sopToText(steps: SopStep[]): string {
  return steps
    .map((step, i) => `${i + 1}. ${step.title}\n${step.instruction}`)
    .join('\n\n');
}

const NUMBERED_LINE = /^\d+[.)]\s+(.*)$/;

/**
 * Parse edited SOP text back into steps. Tools and data sources carry over by
 * step position because the text editor does not show them.
 */
export function parseSopText(text: string, previous: SopStep[]): SopStep[] {
  const drafts: { title: string; lines: string[] }[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line === '') continue;
    const numbered = NUMBERED_LINE.exec(line);
    if (numbered) {
      drafts.push({ title: numbered[1].trim(), lines: [] });
    } else if (drafts.length > 0) {
      drafts[drafts.length - 1].lines.push(line);
    }
  }
  return drafts
    .filter((draft) => draft.title !== '')
    .map((draft, i) => ({
      title: draft.title,
      instruction: draft.lines.join(' '),
      uses: previous[i]?.uses ?? [],
    }));
}

export function replayDurationMs(
  replay: ReplayDef,
  defaultStepMs: number,
): number {
  return replay.steps.reduce(
    (sum, step) => sum + (step.durationMs ?? defaultStepMs),
    0,
  );
}

export interface ConfigEvent {
  id: string;
  agentId: AgentId;
  atMs: number;
  action: string;
  detail: string;
}

/** Configuration changes appear in the audit log next to agent actions. */
export function configEventToEntry(event: ConfigEvent): ActivityEntry {
  return {
    id: event.id,
    agentId: event.agentId,
    atMs: event.atMs,
    action: event.action,
    targets: [],
    detail: event.detail,
    outcome: { label: 'Applied', tone: 'success' },
    approver: { kind: 'config', personId: PERSONA_ID },
    reasoning: [],
  };
}

export function sortByRecency(entries: ActivityEntry[]): ActivityEntry[] {
  return [...entries].sort((a, b) => b.atMs - a.atMs);
}

/** One roster table row. Text fields feed search, sorting and the CSV. */
export interface RosterRow {
  id: AgentId;
  agent: string;
  /** One line on what the agent does, shown under the name. */
  short: string;
  status: string;
  autonomy: string;
  actions: number;
  accepted: number;
  hours: number;
  impact: string;
  statusKey: AgentStatus;
  autonomyKey: AutonomyLevel;
  impactValue: string;
  impactLabel: string;
  /** How many agents this one coordinates; set only on the director. */
  coordinates: number | undefined;
}

export function rosterRow(
  agent: AgentDef,
  level: AutonomyLevel,
  status: AgentStatus,
): RosterRow {
  const { metrics } = agent;
  return {
    id: agent.id,
    agent: agent.name,
    short: agent.short,
    status: STATUS_LABEL[status],
    autonomy: AUTONOMY_SHORT_LABEL[level],
    actions: metrics.actions,
    accepted: metrics.acceptancePct,
    hours: metrics.hoursSaved,
    impact: `${metrics.impact.value} ${metrics.impact.label}`,
    statusKey: status,
    autonomyKey: level,
    impactValue: metrics.impact.value,
    impactLabel: metrics.impact.label,
    coordinates: agent.coordinates?.length,
  };
}

/** Section number from a clause id like 'SA-CORV-2024-017:4.2'. */
function clauseSection(clause: string): string {
  const colon = clause.indexOf(':');
  return colon === -1 ? clause : clause.slice(colon + 1);
}

/** What a link to this entity reads as: an id, a name or a page label. */
export function entityRefText(entityRef: EntityRef): string {
  switch (entityRef.kind) {
    case 'supplier':
      return getSupplier(entityRef.id).shortName;
    case 'material':
      return getMaterial(entityRef.id).name;
    case 'contract':
      return entityRef.clause
        ? `${entityRef.id} \u00a7${clauseSection(entityRef.clause)}`
        : entityRef.id;
    case 'page':
      return entityRef.label;
    default:
      return entityRef.id;
  }
}

/** The approver as plain text, for search and the CSV. */
export function approverText(approver: Approver): string {
  if (approver.kind === 'autonomous') return 'Autonomous';
  const prefix =
    approver.kind === 'approved'
      ? 'Approved by'
      : approver.kind === 'pending'
        ? 'Awaiting'
        : 'Changed by';
  return `${prefix} ${getPerson(approver.personId).name}`;
}

/** One audit-table row. Text fields feed search, sorting and the CSV. */
export interface ActivityRow {
  id: string;
  time: string;
  agent: string;
  action: string;
  target: string;
  outcome: string;
  approver: string;
  entry: ActivityEntry;
}

export function activityRow(
  entry: ActivityEntry,
  agentName: string,
): ActivityRow {
  return {
    id: entry.id,
    time: fmtClock(entry.atMs),
    agent: agentName,
    action: entry.action,
    target: entry.targets.map(entityRefText).join(', '),
    outcome: entry.outcome.label,
    approver: approverText(entry.approver),
    entry,
  };
}
